import { ReactNode, useState, useEffect, useMemo } from 'react';
import { generatePath, useNavigate } from 'react-router';
import { useParams } from 'react-router-dom';
import { Button, Box, Grid, Typography, Stack } from '@mui/material';
import SpeakerNotesIcon from '@mui/icons-material/SpeakerNotes';
import CreateIcon from '@mui/icons-material/Create';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import SendIcon from '@mui/icons-material/Send';
import {
  PlanButtonsWrapper,
  SalonInfoWrapper,
  SalonButtonsWrapper,
  StyledIconButton,
  ResendPlanButtonWrapper
} from './Salon.styles';
import URLs from '../URLs';
import {
  ControlBar,
  TableComponent,
  MainContainer,
  InfoBlock,
  useNotification,
  Loader,
  ControlButton,
  ResendPlanModal,
} from '../../components';
import NotesModal from './NotesModal';
import { useSalonStore } from '../../store';
import { months, SalonPlansColumns } from './types';
import { useTranslation } from 'react-i18next';
import { filterAndRenameObjectKeys, LocalizedColumns } from '../../utils';
import { SalonActionPlan, SalonInfoMap } from '../../types';
import { getObjectValuesAsString, isObject } from '../../utils/PreparedObjects';

const Salon = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { salonId, sent } = useParams();
  const { isLoading, salon, fetchSalon, salonActionPlan, fetchSalonActionPlanList, resetStore } =
    useSalonStore();

  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);

  const handleNotesModalOpen = () => setIsNotesModalOpen(true);
  const handleNotesModalClose = () => setIsNotesModalOpen(false);
  const { showMessage } = useNotification();

  useEffect(() => {
    if (sent === 'sent') showMessage(t('notifications.dataSaved'), 'success');
  }, [sent]);

  useEffect(() => {
    if (salonId) {
      fetchSalonActionPlanList(salonId, showMessage, navigate);
      fetchSalon(salonId, showMessage, navigate);
    }
  }, [salonId]);

  const getDate = (timeStamp: string) => {
    const date = new Date(parseInt(timeStamp) * 1000);
    const month = t(`months.${months[date.getMonth()]}`);

    return `${month} ${date.getDate()}, ${date.getFullYear()}`;
  };

  const handleSendButton = (id: string) => {
    setSelectedRowId(id);
    setIsSendModalOpen(true);
  };

  const rowsPrepare = (rows: SalonActionPlan[], icon?: ReactNode) => {
    return rows.map((row) => {
      return {
        ...row,
        lastModified: (
          <Stack justifyContent="space-between">
            {getDate(row.changed)}
            {row.planIsCompleted === 'true' &&
              (
                <ControlButton
                  label={(
                    <>
                      <SendIcon /> {t('send')}
                    </>
                  )}
                  variant="contained"
                  action={() => (handleSendButton(row.id))}
                  dataTestId='save-and-send'
                />
              )
            }
            <StyledIconButton
              onClick={() =>
                navigate(
                  generatePath(
                    `${URLs.Salon}/${row.planIsCompleted === 'true' ? URLs.ActionPlan : URLs.NewPlan}`,
                    {
                      salonId,
                      planId: row.id,
                      year: row.year,
                      quarter: row.quarter[1],
                      completed: row.planIsCompleted === 'true' ? 'completed' : null,
                    },
                  ),
                )
              }
            >
              {icon || <ChevronRightRoundedIcon />}
            </StyledIconButton>
          </Stack>
        ),
      };
    });
  };

  const uncompletedPlans = useMemo(
    () => Object.values(salonActionPlan).filter((plan) => plan.planIsCompleted !== 'true'),
    [salonActionPlan],
  );
  const completedPlans = useMemo(
    () => Object.values(salonActionPlan).filter((plan) => plan.planIsCompleted === 'true'),
    [salonActionPlan],
  );

  const salonAddress = useMemo(
    () =>
      [
        salon?.mAddress1,
        salon?.mAddress2,
        salon?.mCity,
        salon?.mState,
        salon?.mCountry,
        salon?.mZip,
      ]
        .filter(Boolean)
        .map((item) => (isObject(item) ? getObjectValuesAsString(item) : item))
        .join(', '),
    [salon?.mAddress1, salon?.mAddress2, salon?.mCity, salon?.mCountry, salon?.mZip],
  );

  const newPlanCreate = () => {
    resetStore();
    navigate(
      generatePath(`${URLs.Salon}/${URLs.NewPlan}`, {
        salonId,
        planId: null,
        year: null,
        quarter: null,
      }),
    );
  };

  return (
    <>
      <ControlBar
        backButtonAction={() => navigate(URLs.ROOT)}
        title={<Typography variant="h5">{t('salon')}</Typography>}
      />
      <MainContainer>
        <Loader isLoading={isLoading} />
        <Grid container>
          <Grid item xs={12} sm={6} display="flex" alignItems="center">
            <Box>
              <SalonInfoWrapper>
                <Typography variant="h5" textTransform="capitalize">
                  {salon?.title?.toLowerCase()}
                </Typography>
                <Typography variant="body2">{salonAddress}</Typography>
              </SalonInfoWrapper>
              <SalonButtonsWrapper>
                <Button
                  variant="outlined"
                  startIcon={<SpeakerNotesIcon />}
                  onClick={handleNotesModalOpen}
                  data-testid="nottes-btn"
                >
                  {t('notes')}
                </Button>
              </SalonButtonsWrapper>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <PlanButtonsWrapper>
              <Button
                variant="contained"
                disabled={uncompletedPlans?.length > 0}
                onClick={() => newPlanCreate()}
                data-testid="new-plan-btn"
              >
                {t('newPlan')}
              </Button>
            </PlanButtonsWrapper>
          </Grid>
        </Grid>
        <InfoBlock infoList={filterAndRenameObjectKeys(salon, SalonInfoMap)} />
        <TableComponent
          tableName={t('salonPage.incompleteTableName')}
          rows={rowsPrepare(uncompletedPlans, <CreateIcon />)}
          columns={LocalizedColumns(SalonPlansColumns, t)}
        />
        <NotesModal open={isNotesModalOpen} handleClose={handleNotesModalClose} />
        <ResendPlanButtonWrapper>
          <TableComponent
            tableName={t('salonPage.completeTableName')}
            rows={rowsPrepare(completedPlans)}
            columns={LocalizedColumns(SalonPlansColumns, t)}
          />
        </ResendPlanButtonWrapper>
        <ResendPlanModal
          rowId={selectedRowId}
          open={isSendModalOpen}
          handleClose={() => {
            setIsSendModalOpen(false);
          }}
        />
      </MainContainer>
    </>
  );
};

export default Salon;
